import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../utils/api";
import { Plus, Trash2, Save, Edit3, Check, X, Star } from "lucide-react";

const fmtPrice = (cents, cur) => {
  const sym = { eur: "€", usd: "$", gbp: "£" }[(cur || "").toLowerCase()] || "";
  return cents ? `${sym}${(cents / 100).toFixed(0)}` : "Free";
};

const BLANK = { name: "", kind: "paid", price_cents: 9900, currency: "eur", interval: "month", trial_days: 0, features: [""], highlight: false, sort: 0, active: true };

export default function PricingPlansPage() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);   // id | "new"
  const [draft, setDraft] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = async () => {
    const r = await api.get("/plans/");
    setPlans(r.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setDraft({ ...BLANK, sort: plans.length }); };
  const startEdit = (p) => { setEditing(p.id); setDraft({ ...p, features: [...(p.features || [])] }); };
  const cancel = () => { setEditing(null); setDraft(BLANK); };

  const saveDraft = async () => {
    if (!draft.name.trim()) { setFlash({ type: "error", text: "Name is required" }); return; }
    setSaving(true);
    const clean = { ...draft, features: draft.features.filter(f => f && f.trim()) };
    try {
      if (editing === "new") await api.post("/plans/", clean);
      else await api.put(`/plans/${editing}`, clean);
      setFlash({ type: "success", text: "Saved." });
      cancel();
      await load();
    } catch (e) {
      setFlash({ type: "error", text: e.response?.data?.detail || "Save failed" });
    } finally { setSaving(false); setTimeout(() => setFlash(null), 3000); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    await api.delete(`/plans/${id}`);
    await load();
  };

  const f = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  const setFeature = (i, v) => setDraft(p => ({ ...p, features: p.features.map((x, j) => j === i ? v : x) }));
  const addFeature = () => setDraft(p => ({ ...p, features: [...p.features, ""] }));
  const rmFeature = (i) => setDraft(p => ({ ...p, features: p.features.filter((_, j) => j !== i) }));

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.5rem", margin: 0 }}>Pricing Plans</h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: "0.25rem 0 0" }}>Active plans show up on the public landing page in the order defined below.</p>
          </div>
          <button data-testid="plans-new-btn" onClick={startNew} className="btn-primary" style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.5rem 1rem", fontSize: "0.85rem" }}>
            <Plus size={14} /> New plan
          </button>
        </div>

        {flash && <div data-testid="plans-flash" style={{ marginBottom: "1rem", padding: "0.55rem 0.9rem", borderRadius: 8, background: flash.type === "success" ? "#DCFCE7" : "#FEE2E2", color: flash.type === "success" ? "#166534" : "#991B1B", fontSize: "0.82rem" }}>{flash.text}</div>}

        <div style={{ display: "grid", gap: "0.75rem" }}>
          {plans.map(p => (
            <div key={p.id} data-testid={`plan-row-${p.id}`} className="recon-card" style={{ padding: "1rem 1.2rem" }}>
              {editing === p.id ? (
                <PlanForm draft={draft} f={f} features={draft.features} setFeature={setFeature} addFeature={addFeature} rmFeature={rmFeature} onSave={saveDraft} onCancel={cancel} saving={saving} />
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <h3 style={{ margin: 0, fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.02rem" }}>{p.name}</h3>
                      {p.highlight && <Star size={14} style={{ color: "var(--sage)", fill: "var(--sage)" }} />}
                      {!p.active && <span style={{ fontSize: "0.62rem", padding: "0.1rem 0.4rem", borderRadius: 999, background: "#F3F4F6", color: "#6B7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Inactive</span>}
                    </div>
                    <div style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
                      {fmtPrice(p.price_cents, p.currency)}{p.price_cents ? ` / ${p.interval}` : (p.trial_days ? ` · ${p.trial_days}-day trial` : "")}
                      {" · "}{p.kind}{" · "}<span>{(p.features || []).length} features</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.4rem" }}>
                    <button data-testid={`plan-edit-${p.id}`} onClick={() => startEdit(p)} className="btn-secondary" style={{ display: "flex", alignItems: "center", gap: "0.3rem", padding: "0.4rem 0.8rem", fontSize: "0.8rem" }}><Edit3 size={12} /> Edit</button>
                    <button data-testid={`plan-del-${p.id}`} onClick={() => del(p.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", padding: "0.4rem" }}><Trash2 size={14} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {editing === "new" && (
            <div data-testid="plan-row-new" className="recon-card" style={{ padding: "1rem 1.2rem", borderColor: "var(--sage)" }}>
              <PlanForm draft={draft} f={f} features={draft.features} setFeature={setFeature} addFeature={addFeature} rmFeature={rmFeature} onSave={saveDraft} onCancel={cancel} saving={saving} isNew />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function PlanForm({ draft, f, features, setFeature, addFeature, rmFeature, onSave, onCancel, saving, isNew }) {
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 1fr", gap: "0.6rem", marginBottom: "0.6rem" }}>
        <input data-testid="plan-form-name" className="recon-input" placeholder="Plan name *" value={draft.name} onChange={e => f("name", e.target.value)} />
        <select data-testid="plan-form-kind" className="recon-input" value={draft.kind} onChange={e => f("kind", e.target.value)}>
          <option value="paid">Paid</option>
          <option value="free">Free / trial</option>
        </select>
        <input data-testid="plan-form-price" type="number" className="recon-input" placeholder="Price (cents)" value={draft.price_cents} onChange={e => f("price_cents", Number(e.target.value))} />
        <select data-testid="plan-form-currency" className="recon-input" value={draft.currency} onChange={e => f("currency", e.target.value)}>
          <option value="eur">EUR</option><option value="usd">USD</option><option value="gbp">GBP</option>
        </select>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0.6rem", marginBottom: "0.6rem" }}>
        <select data-testid="plan-form-interval" className="recon-input" value={draft.interval} onChange={e => f("interval", e.target.value)}>
          <option value="month">Monthly</option><option value="year">Yearly</option><option value="one_time">One-time</option>
        </select>
        <input data-testid="plan-form-trial" type="number" className="recon-input" placeholder="Trial days" value={draft.trial_days} onChange={e => f("trial_days", Number(e.target.value))} />
        <input data-testid="plan-form-sort" type="number" className="recon-input" placeholder="Sort (asc)" value={draft.sort} onChange={e => f("sort", Number(e.target.value))} />
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.82rem" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <input data-testid="plan-form-highlight" type="checkbox" checked={!!draft.highlight} onChange={e => f("highlight", e.target.checked)} /> Highlight
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <input data-testid="plan-form-active" type="checkbox" checked={draft.active !== false} onChange={e => f("active", e.target.checked)} /> Active
          </label>
        </div>
      </div>
      <div>
        <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Features</label>
        {features.map((ft, i) => (
          <div key={i} style={{ display: "flex", gap: "0.4rem", marginTop: "0.4rem" }}>
            <input data-testid={`plan-feature-${i}`} className="recon-input" value={ft} onChange={e => setFeature(i, e.target.value)} placeholder="e.g. Unlimited reconciliations" />
            <button onClick={() => rmFeature(i)} style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer" }}><X size={14} /></button>
          </div>
        ))}
        <button onClick={addFeature} className="btn-secondary" style={{ marginTop: "0.4rem", fontSize: "0.75rem", padding: "0.3rem 0.75rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}><Plus size={11} /> Add feature</button>
      </div>
      <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.8rem" }}>
        <button data-testid="plan-save-btn" onClick={onSave} disabled={saving} className="btn-primary" style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", padding: "0.45rem 1.1rem", fontSize: "0.82rem" }}>
          <Check size={13} /> {saving ? "Saving…" : (isNew ? "Create plan" : "Save")}
        </button>
        <button data-testid="plan-cancel-btn" onClick={onCancel} className="btn-secondary" style={{ padding: "0.45rem 1rem", fontSize: "0.82rem" }}>Cancel</button>
      </div>
    </div>
  );
}
